import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function AdminPasswordReset() {
  const navigate = useNavigate();
  const [token] = useState(() => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const value = params.get("token") || "";

    if (window.location.hash) {
      window.history.replaceState(
        null,
        document.title,
        `${window.location.pathname}${window.location.search}`
      );
    }

    return value;
  });

  const [account, setAccount] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Missing reset token.");
      setLoading(false);
      return;
    }

    api.post("/auth/password-reset/validate", { token })
      .then(({ data }) => setAccount(data))
      .catch((err) => {
        setError(
          err?.response?.data?.detail ||
            "This reset link is invalid or has expired."
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);

    try {
      await api.post("/auth/password-reset", {
        token,
        password,
      });

      setComplete(true);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Unable to reset password."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-lightbg px-5 py-10">
      <div className="mx-auto max-w-lg">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        <div className="rounded-md border border-brandborder bg-white p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="rounded-sm bg-navy p-2.5 text-cyan">
              <KeyRound size={22} />
            </div>

            <div>
              <h1 className="font-heading text-2xl font-bold text-navy">
                Reset password
              </h1>
              {account?.email && (
                <p className="mt-1 text-sm text-slate">{account.email}</p>
              )}
            </div>
          </div>

          {loading ? (
            <div className="mt-7 text-sm text-slate">Validating link...</div>
          ) : error ? (
            <div className="mt-7 rounded-sm border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {error}
            </div>
          ) : complete ? (
            <div className="mt-7">
              <p className="text-sm text-slate">
                Your password has been reset. You can now sign in using your
                new password and existing MFA.
              </p>

              <button
                type="button"
                onClick={() => navigate("/admin/login")}
                className="mt-6 w-full rounded-sm bg-navy px-4 py-3 text-sm font-semibold text-white"
              >
                Continue to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-4">
              <input
                type="password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password"
                className="w-full rounded-sm border border-brandborder px-3 py-2.5"
              />

              <input
                type="password"
                required
                minLength={12}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="w-full rounded-sm border border-brandborder px-3 py-2.5"
              />

              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-sm bg-navy px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Resetting..." : "Reset password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
